import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, Button, Input, Field, FieldLabel } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import toast from 'react-hot-toast';
import type { PlatformSubscriptionCoupon } from '@court-sync/shared';

export default function SubscriptionCouponsPage() {
  const [coupons, setCoupons] = useState<PlatformSubscriptionCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: '',
    name: '',
    percent_off: '' as string | number,
    amount_off: '' as string | number,
    currency: 'usd',
    duration: 'once' as 'once' | 'repeating' | 'forever',
    duration_in_months: '' as string | number,
    valid_until: '',
  });

  const loadCoupons = () => {
    setLoading(true);
    adminBackofficeApi
      .getSubscriptionCoupons()
      .then((data: { success?: boolean; data?: PlatformSubscriptionCoupon[] }) => {
        if (data.success && Array.isArray(data.data)) {
          setCoupons(data.data);
        } else {
          setError('Failed to load coupons');
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error('Code is required');
      return;
    }
    if (form.percent_off === '' && form.amount_off === '') {
      toast.error('Enter percent off or amount off');
      return;
    }
    if (form.percent_off !== '' && form.amount_off !== '') {
      toast.error('Use either percent off or amount off, not both');
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        code: form.code.trim(),
        name: form.name.trim() || undefined,
        duration: form.duration,
        valid_until: form.valid_until || undefined,
      };
      if (form.percent_off !== '') {
        body.percent_off = Number(form.percent_off);
      } else {
        body.amount_off = Math.round(Number(form.amount_off) * 100); // dollars to cents
        body.currency = form.currency;
      }
      if (form.duration === 'repeating' && form.duration_in_months !== '') {
        body.duration_in_months = Number(form.duration_in_months);
      }
      const res = await adminBackofficeApi.createSubscriptionCoupon(body as any);
      if (res.success) {
        toast.success('Coupon created');
        setShowForm(false);
        setForm({
          code: '',
          name: '',
          percent_off: '',
          amount_off: '',
          currency: 'usd',
          duration: 'once',
          duration_in_months: '',
          valid_until: '',
        });
        loadCoupons();
      } else {
        toast.error(res.error || 'Failed to create');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Deactivate this coupon? It will no longer be usable.')) return;
    try {
      const res = await adminBackofficeApi.deactivateSubscriptionCoupon(id);
      if (res.success) {
        toast.success('Coupon deactivated');
        loadCoupons();
      } else {
        toast.error(res.error || 'Failed to deactivate');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to deactivate');
    }
  };

  if (error && coupons.length === 0) {
    return (
      <div>
        <PageHeader
          title="Subscription coupons"
          help={
            <p>
              Create promo codes for coaches subscribing to CourtSync. Coaches enter the code when
              signing up for an academy owner subscription.
            </p>
          }
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Subscription coupons"
        help={
          <p>
            Create promo codes for coaches subscribing to CourtSync. Coaches enter the code when
            signing up for an academy owner subscription.
          </p>
        }
      />

      {showForm ? (
        <Card className="mb-6">
          <CardHeader>
            <h2 className="text-lg font-medium">Create coupon</h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4 max-w-xl">
              <Field>
                <FieldLabel htmlFor="coupon-code">Code</FieldLabel>
                <Input id="coupon-code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. LAUNCH20" className="uppercase" required />
              </Field>
              <Field>
                <FieldLabel htmlFor="coupon-name">Name (optional)</FieldLabel>
                <Input id="coupon-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Launch promo" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <FieldLabel htmlFor="coupon-percent">Percent off (e.g. 20)</FieldLabel>
                  <Input id="coupon-percent" type="number" min={0} max={100} value={form.percent_off} onChange={(e) => setForm((f) => ({ ...f, percent_off: e.target.value, amount_off: '' }))} placeholder="20" />
                </Field>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Or amount off (e.g. 10 = $10)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.amount_off}
                      onChange={(e) => setForm((f) => ({ ...f, amount_off: e.target.value, percent_off: '' }))}
                      placeholder="10"
                      className="input flex-1"
                    />
                    <select
                      value={form.currency}
                      onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                      className="input w-20"
                    >
                      <option value="usd">USD</option>
                      <option value="aud">AUD</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Duration</label>
                  <select
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value as 'once' | 'repeating' | 'forever' }))}
                    className="input w-full"
                  >
                    <option value="once">Once (first charge only)</option>
                    <option value="repeating">Repeating (N months)</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>
                {form.duration === 'repeating' && (
                  <Field>
                    <FieldLabel htmlFor="coupon-duration-months">Duration in months</FieldLabel>
                    <Input id="coupon-duration-months" type="number" min={1} value={form.duration_in_months} onChange={(e) => setForm((f) => ({ ...f, duration_in_months: e.target.value }))} placeholder="3" />
                  </Field>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Platform coupons are limited to one use per user (unlimited total uses).
              </p>
              <Field>
                <FieldLabel htmlFor="coupon-valid-until">Valid until (optional)</FieldLabel>
                <Input id="coupon-valid-until" type="datetime-local" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} />
              </Field>
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create coupon'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button className="mb-6" onClick={() => setShowForm(true)}>
          Create coupon
        </Button>
      )}

      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">Existing coupons</h2>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : coupons.length === 0 ? (
            <p className="text-gray-500">No coupons yet. Create one to offer coaches a discount when they subscribe.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Code</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Redemptions</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Valid until</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Active</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {coupons.map((c) => (
                    <tr key={c.id}>
                      <td className="px-4 py-2 text-sm font-mono">{c.code}</td>
                      <td className="px-4 py-2 text-sm">{c.name ?? '–'}</td>
                      <td className="px-4 py-2 text-sm" title={c.maxRedemptions == null ? 'One use per user' : undefined}>
                        {c.redemptionCount}
                        {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ' (once per user)'}
                      </td>
                      <td className="px-4 py-2 text-sm">{c.validUntil ? new Date(c.validUntil).toLocaleString() : '–'}</td>
                      <td className="px-4 py-2 text-sm">{c.active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2">
                        {c.active && (
                          <Button variant="outline" size="sm" onClick={() => handleDeactivate(c.id)}>
                            Deactivate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
